import React, {useCallback, useEffect, useMemo, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    deleteTransaction,
    getTransactions,
    updateTransaction,
    updateTransactionCategory
} from "../api/transactions";
import {
    buildTransactionTableSearchHaystack,
    formatDateToKyiv,
    isRealIncome,
    isUncategorized,
} from "../helpers/utils";
import { useFopLabels } from "../hooks/useFopLabels";
import { CATEGORIES, TIMEZONE } from "../constans";
import CardLabel from "../helpers/CardLabel";
import { DeleteTransactionModal, EditTransactionModal } from "../components/TransactionModals";
import ManualTransactionModal from "../components/ManualTransactionModal";
import {Button, Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import { Pencil, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import Loader from "../components/common/Loader";
import CounterTags from "../components/CounterTags";
import MonthTransactionsChart, {
    TX_DRILL_CHART_KINDS,
    TX_DRILL_CHART_STORAGE_KEY,
} from "../components/MonthTransactionsChart";
import { AppDateField } from "../components/form";

const TX_DRILL_VALID_KINDS = new Set(TX_DRILL_CHART_KINDS.map(k => k.id));

function formatTxDescription(raw, maxLen = 38) {
    if (!raw) return "";
    let s = raw.replace(/^від:\s*/i, "").replace(/^Фізична особа-підприємець\s*/i, "ФОП ");
    if (s.length > maxLen) s = s.slice(0, maxLen) + "…";
    return s;
}

/** Не показуємо в таблиці та не враховуємо в денному графіку. */
function isInternalTransferTransaction(tx) {
    return tx.isInternal === true || tx.category === "Внутрішні перекази";
}

const TransactionsByTypePage = ({ user, type }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const selectedCategory = searchParams.get("category");
    const selectedDirection = searchParams.get("direction"); // "income" | "expense" | null
    const monthFromQuery = searchParams.get("month");

    const defaultMonth =
        monthFromQuery ||
        localStorage.getItem("selectedMonth") ||
        new Date().toISOString().slice(0, 7);

    const [selectedMonth] = useState(defaultMonth);
    const [transactions, setTransactions] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: "time", direction: "desc" });

    const [editTx, setEditTx] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [dayModal, setDayModal] = useState(null);
    const [chartKind, setChartKind] = useState(() => {
        try {
            const stored = localStorage.getItem(TX_DRILL_CHART_STORAGE_KEY);
            if (stored && TX_DRILL_VALID_KINDS.has(stored)) return stored;
        } catch (_) {
            /* ignore */
        }
        return "groupedBar";
    });

    /** Фільтр таблиці за календарним днем (TZ застосунку): усі / сьогодні / вчора / дата з picker. */
    const [tableDayFilter, setTableDayFilter] = useState("all");
    const [tablePickDate, setTablePickDate] = useState("");
    const [showTableIncome, setShowTableIncome] = useState(true);
    const [showTableExpense, setShowTableExpense] = useState(true);
    const [showManualPayment, setShowManualPayment] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(TX_DRILL_CHART_STORAGE_KEY, chartKind);
        } catch (_) {
            /* ignore */
        }
    }, [chartKind]);

    const isAdmin = user?.role === "admin";
    const { labelBySlug } = useFopLabels();

    // ======================
    // LOAD TRANSACTIONS
    // ======================
    const loadTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const start = dayjs(`${selectedMonth}-01`).startOf("month").toISOString();

            const end = dayjs(`${selectedMonth}-01`).endOf("month").endOf("day").toISOString();

            const allRaw = await getTransactions(start, end);

            const all =
                user?.role === "admin"
                    ? allRaw
                    : allRaw.filter(
                          tx =>
                              !(tx.categoryType === "personal" && tx.direction === "income")
                      );

            let filteredByType;

            if (type === "cash") {
                filteredByType = all.filter(tx => tx.paymentType === "cash");
            } else {
                filteredByType = all.filter(tx => tx.categoryType === type);
            }

            let filtered = filteredByType;

            if (selectedCategory === "Без категорії") {
                filtered = filtered.filter(isUncategorized);
            } else if (selectedCategory) {
                filtered = filtered.filter(tx => tx.category === selectedCategory);
            }

            if (selectedDirection === "income") {
                filtered = filtered.filter(tx => {
                    if (tx.direction !== "income") return false;
                    if (type === "cash") return tx.paymentType === "cash";
                    return isRealIncome(tx);
                });
            }

            if (selectedDirection === "expense") {
                filtered = filtered.filter(tx => tx.direction === "expense" && !tx.isInternal);
            }

            filtered = filtered.filter(tx => !isInternalTransferTransaction(tx));

            setTransactions(filtered);
        } catch (e) {
            console.error("Error loading transactions", e);
        } finally {
            setLoading(false);
        }
    }, [type, selectedMonth, selectedCategory, selectedDirection, user?.role]);

    useEffect(() => {
        loadTransactions();
    }, [loadTransactions]);


    const searchedTransactions = useMemo(() => {
        const tokens = search
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
        if (!tokens.length) return transactions;

        return transactions.filter(tx => {
            const description = (tx.description || "").toLowerCase();
            const comment = (tx.comment || "").toLowerCase();

            // сума — у гривнях; один рядок на всю транзакцію — кожен токен має десь зустрітись
            const amount = Math.abs(tx.amount / 100).toString();
            const tableFields = buildTransactionTableSearchHaystack(tx, labelBySlug);
            const haystack = `${description} ${comment} ${amount} ${tableFields}`;

            return tokens.every(t => haystack.includes(t));
        });
    }, [transactions, search, labelBySlug]);

    const todayStrKyiv = dayjs().tz(TIMEZONE).format("YYYY-MM-DD");
    const yesterdayStrKyiv = dayjs().tz(TIMEZONE).subtract(1, "day").format("YYYY-MM-DD");
    const todayInLoadedMonth = todayStrKyiv.slice(0, 7) === selectedMonth;
    const yesterdayInLoadedMonth = yesterdayStrKyiv.slice(0, 7) === selectedMonth;

    const pickDateBounds = useMemo(() => {
        const start = dayjs(`${selectedMonth}-01`).tz(TIMEZONE).startOf("month");
        return {
            min: start.format("YYYY-MM-DD"),
            max: start.endOf("month").format("YYYY-MM-DD"),
        };
    }, [selectedMonth]);

    const resolvedTableDayIso = useMemo(() => {
        if (tableDayFilter === "all") return null;
        if (tableDayFilter === "today") return todayInLoadedMonth ? todayStrKyiv : null;
        if (tableDayFilter === "yesterday") return yesterdayInLoadedMonth ? yesterdayStrKyiv : null;
        if (tableDayFilter === "pick" && tablePickDate) return tablePickDate;
        return null;
    }, [
        tableDayFilter,
        tablePickDate,
        todayInLoadedMonth,
        yesterdayInLoadedMonth,
        todayStrKyiv,
        yesterdayStrKyiv,
    ]);

    /** Дефолтна дата у формі ручного платежу: вибраний день таблиці, інакше завжди реальне «сьогодні» (а не 1 число переглянутого місяця). */
    const manualPresetTimeYMD = useMemo(() => {
        if (resolvedTableDayIso) return resolvedTableDayIso;
        return dayjs().tz(TIMEZONE).format("YYYY-MM-DD");
    }, [resolvedTableDayIso]);

    const dayScopedTransactions = useMemo(() => {
        if (!resolvedTableDayIso) return searchedTransactions;
        return searchedTransactions.filter(
            tx => dayjs(tx.time).tz(TIMEZONE).format("YYYY-MM-DD") === resolvedTableDayIso
        );
    }, [searchedTransactions, resolvedTableDayIso]);

    const tableDirectionFiltered = useMemo(() => {
        return dayScopedTransactions.filter(tx => {
            if (tx.direction === "income" && !showTableIncome) return false;
            if (tx.direction === "expense" && !showTableExpense) return false;
            return true;
        });
    }, [dayScopedTransactions, showTableIncome, showTableExpense]);

    const groupedByDay = useMemo(() => {
        const map = {};

        transactions.forEach(tx => {
            const day = dayjs(tx.time).format("YYYY-MM-DD");

            if (!map[day]) {
                map[day] = {
                    date: day,
                    income: [],
                    expense: [],
                };
            }

            if (
                tx.direction === "income" &&
                isRealIncome(tx) &&
                tx.category !== "Внутрішні перекази"
            ) {
                map[day].income.push(tx);
            }

            const isExcluded =
                tx.description === "Комісія еквайрингу Monobank" ||
                tx.category === "Зняття готівки";

            const isInternalTransfer =
                tx.isInternal === true || tx.category === "Внутрішні перекази";

            if (tx.direction === "expense" && !isExcluded && !isInternalTransfer) {
                map[day].expense.push(tx);
            }

        });

        return Object.values(map).sort((a, b) =>
            dayjs(a.date).unix() - dayjs(b.date).unix()
        );
    }, [transactions]);

    useEffect(() => {
        transactions.forEach(tx => {
            if (!Number.isFinite(Number(tx.amount))) {
                console.log("🚨 BAD AMOUNT:", {
                    id: tx._id,
                    amount: tx.amount,
                    type: typeof tx.amount,
                    description: tx.description,
                    time: tx.time,
                    full: tx
                });
            }
        });
    }, [transactions]);


    /** Дні календарного місяця до «сьогодні» включно в TZ застосунку; минулі місяці — повний місяць (без лінії на майбутні дні поточного місяця). */
    const fullMonthChartData = useMemo(() => {
        const byDate = {};
        groupedByDay.forEach(day => {
            byDate[day.date] = {
                date: day.date,
                income: day.income.reduce((s, tx) => s + Math.abs(tx.amount), 0) / 100,
                expense: day.expense.reduce((s, tx) => s + Math.abs(tx.amount), 0) / 100,
                incomeTxs: day.income,
                expenseTxs: day.expense,
            };
        });

        const start = dayjs(`${selectedMonth}-01`).startOf("month");
        const daysInMonth = start.daysInMonth();

        const todayYm = dayjs().tz(TIMEZONE).format("YYYY-MM");
        let maxDayIndex = daysInMonth;
        if (selectedMonth === todayYm) {
            maxDayIndex = Math.min(daysInMonth, dayjs().tz(TIMEZONE).date());
        }

        const rows = [];
        for (let i = 1; i <= maxDayIndex; i++) {
            const date = start.date(i).format("YYYY-MM-DD");
            const existing = byDate[date];
            rows.push({
                dayIndex: i,
                date,
                income: existing?.income ?? 0,
                expense: existing?.expense ?? 0,
                incomeTxs: existing?.incomeTxs ?? [],
                expenseTxs: existing?.expenseTxs ?? [],
            });
        }
        return rows;
    }, [groupedByDay, selectedMonth]);

    /** Легкі точки без масивів транзакцій — Recharts та tooltip не тягнуть сотні об'єктів при русі миші. */
    const monthChartPlotData = useMemo(
        () =>
            fullMonthChartData.map(({ incomeTxs, expenseTxs, ...plot }) => plot),
        [fullMonthChartData]
    );

    const monthDayDetailMap = useMemo(() => {
        const m = new Map();
        fullMonthChartData.forEach(row => {
            m.set(row.date, {
                incomeTxs: row.incomeTxs,
                expenseTxs: row.expenseTxs,
            });
        });
        return m;
    }, [fullMonthChartData]);

    const sortedTransactions = useMemo(() => {
        return [...tableDirectionFiltered].sort((a, b) => {
            const key = sortConfig.key;

            let aValue = a[key];
            let bValue = b[key];

            if (key === "time") {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            } else {
                aValue = (aValue ?? "").toString().toLowerCase();
                bValue = (bValue ?? "").toString().toLowerCase();
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [tableDirectionFiltered, sortConfig]);

    const matchesCurrentView = (tx) => {
        if (isInternalTransferTransaction(tx)) return false;

        if (tx.categoryType !== type) return false;

        if (selectedCategory === "Без категорії") {
            return !tx.category;
        }

        if (selectedCategory) {
            return tx.category === selectedCategory;
        }

        if (selectedDirection === "income") {
            return isRealIncome(tx);
        }

        if (selectedDirection === "expense") {
            return tx.direction === "expense";
        }

        return true;
    };


    // ======================
    // HANDLERS
    // ======================

    const handleCommentChange = async (txId, comment) => {
        await updateTransactionCategory(txId, { comment });
        setTransactions(prev =>
            prev.map((tx) =>
                tx._id === txId ? { ...tx, comment, userEditedComment: true } : tx
            )
        );
    };

    const handleTypeChange = async (tx, newType) => {
        await updateTransactionCategory(tx._id, {
            categoryType: newType,
            direction: tx.direction,
            isManual: true,
        });

        const updatedTx = { ...tx, categoryType: newType, isManual: true };

        setTransactions(prev => {
            if (!matchesCurrentView(updatedTx)) {
                return prev.filter(t => t._id !== tx._id);
            }

            return prev.map(t =>
                t._id === tx._id ? updatedTx : t
            );
        });
    };

    const handleCategoryChange = async (tx, category) => {
        await updateTransactionCategory(tx._id, {
            categoryType: tx.categoryType,
            category,
            isManual: true
        });

        const updatedTx = { ...tx, category, isManual: true };

        setTransactions(prev => {
            // если транзакция больше не подходит — убираем её из списка
            if (!matchesCurrentView(updatedTx)) {
                return prev.filter(t => t._id !== tx._id);
            }

            // иначе просто обновляем
            return prev.map(t =>
                t._id === tx._id ? updatedTx : t
            );
        });
    };


    // ======================
    // SORTING
    // ======================
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    const handleBarClick = useCallback((dayPayload, direction) => {
        const detail = monthDayDetailMap.get(dayPayload.date);
        const incomeTxs = detail?.incomeTxs ?? [];
        const expenseTxs = detail?.expenseTxs ?? [];
        const transactions =
            direction === "all"
                ? [...incomeTxs, ...expenseTxs]
                : direction === "income"
                    ? incomeTxs
                    : expenseTxs;
        setDayModal({
            date: dayPayload.date,
            transactions,
            direction,
        });
    }, [monthDayDetailMap]);



    // ======================
    // RENDER
    // ======================
    return (
        <div className="p-4 tx-drill-page">

            {/* HEADER */}
            <div className="d-flex align-items-center gap-3 mb-3">
                <button
                    type="button"
                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                    onClick={() => navigate(-1)}
                >
                    <svg
                        className="tx-drill-back-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            d="M8.5 2.5 3.5 7l5 4.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    Назад
                </button>

                <h3 className="m-0">
                    {type === "business" && "Бізнес"}
                    {type === "personal" && "Особисті"}
                    {type === "cash" && "Готівка"}

                    {selectedCategory && (
                        <span className="text-muted ms-2">/ {selectedCategory}</span>
                    )}
                </h3>

            </div>
            {isAdmin && (
                <div className="mb-5">
                    <MonthTransactionsChart
                        key={`${selectedMonth}-${type}-${selectedCategory ?? ""}-${selectedDirection ?? ""}`}
                        data={monthChartPlotData}
                        chartKind={chartKind}
                        onChartKindChange={setChartKind}
                        onSegmentClick={handleBarClick}
                    />
                </div>
            )}
            {/* ФІЛЬТР ЗА ДНЕМ (над пошуком) + ПОШУК */}
            <div className="mb-3 tx-drill-toolbar">
                <div className="d-flex flex-wrap gap-2 mb-2 tx-drill-toolbar__day-row">
                    <div
                        className="btn-group m-0"
                        role="group"
                        aria-label="Фільтр таблиці за днем"
                    >
                        <button
                            type="button"
                            className={`btn btn-outline-secondary${tableDayFilter === "all" ? " active" : ""}`}
                            onClick={() => {
                                setTableDayFilter("all");
                                setTablePickDate("");
                            }}
                        >
                            Усі дні
                        </button>
                        <button
                            type="button"
                            className={`btn btn-outline-secondary${tableDayFilter === "today" ? " active" : ""}`}
                            disabled={!todayInLoadedMonth}
                            title={
                                todayInLoadedMonth
                                    ? "Лише операції за сьогодні (за київським календарем)"
                                    : "Сьогодні поза межами відкритого місяця — оберіть день або перемкніть місяць"
                            }
                            onClick={() => {
                                setTableDayFilter("today");
                                setTablePickDate("");
                            }}
                        >
                            Сьогодні
                        </button>
                        <button
                            type="button"
                            className={`btn btn-outline-secondary${tableDayFilter === "yesterday" ? " active" : ""}`}
                            disabled={!yesterdayInLoadedMonth}
                            title={
                                yesterdayInLoadedMonth
                                    ? "Лише операції за вчора"
                                    : "Вчора поза межами відкритого місяця"
                            }
                            onClick={() => {
                                setTableDayFilter("yesterday");
                                setTablePickDate("");
                            }}
                        >
                            Вчора
                        </button>
                    </div>
                    <div
                        className="d-flex align-items-center gap-2 tx-drill-toolbar__dir-squares"
                        role="group"
                        aria-label="Тип операцій у таблиці"
                    >
                        <button
                            type="button"
                            className={`tx-drill-dir-square tx-drill-dir-square--income${showTableIncome ? " tx-drill-dir-square--on" : ""}`}
                            aria-pressed={showTableIncome}
                            aria-label={
                                showTableIncome ? "Приховати доходи в таблиці" : "Показувати доходи в таблиці"
                            }
                            title={
                                showTableIncome
                                    ? "Доходи: показано — клік, щоб приховати"
                                    : "Доходи: приховано — клік, щоб показати"
                            }
                            onClick={() => setShowTableIncome(v => !v)}
                        />
                        <button
                            type="button"
                            className={`tx-drill-dir-square tx-drill-dir-square--expense${showTableExpense ? " tx-drill-dir-square--on" : ""}`}
                            aria-pressed={showTableExpense}
                            aria-label={
                                showTableExpense ? "Приховати витрати в таблиці" : "Показувати витрати в таблиці"
                            }
                            title={
                                showTableExpense
                                    ? "Витрати: показано — клік, щоб приховати"
                                    : "Витрати: приховано — клік, щоб показати"
                            }
                            onClick={() => setShowTableExpense(v => !v)}
                        />
                    </div>
                    <label className="d-flex gap-2 mb-0 small text-nowrap tx-drill-toolbar__day-label">
                        <span className="text-muted tx-drill-toolbar__day-label-caption">
                            День:
                        </span>
                        <div className="holder-datapickers mb-0">
                            <AppDateField
                                value={tableDayFilter === "pick" ? tablePickDate : ""}
                                minDate={pickDateBounds.min}
                                maxDate={pickDateBounds.max}
                                title="Будь-який день у межах відкритого місяця"
                                onChange={ymd => {
                                    if (!ymd) {
                                        setTableDayFilter("all");
                                        setTablePickDate("");
                                        return;
                                    }
                                    setTableDayFilter("pick");
                                    setTablePickDate(ymd);
                                }}
                            />
                        </div>
                    </label>
                    <div className="d-flex align-items-center ms-lg-auto flex-shrink-0">
                        <Button
                            type="button"
                            variant="primary"
                            className="tx-drill-add-payment flex-shrink-0 px-3 fw-semibold lh-1"
                            title="Додати платіж"
                            aria-label="Додати платіж"
                            onClick={() => setShowManualPayment(true)}
                        >
                            +
                        </Button>
                    </div>
                </div>
                <input
                    type="search"
                    className="form-control tx-drill-toolbar-search w-100 mb-2"
                    placeholder="Пошук"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {(search || resolvedTableDayIso || !showTableIncome || !showTableExpense) && (
                    <div className="d-flex flex-wrap gap-2">
                        {search && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setSearch("")}
                            >
                                Очистити пошук
                            </button>
                        )}
                        {resolvedTableDayIso && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setTableDayFilter("all");
                                    setTablePickDate("");
                                }}
                            >
                                Скинути фільтр дня
                            </button>
                        )}
                        {(!showTableIncome || !showTableExpense) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    setShowTableIncome(true);
                                    setShowTableExpense(true);
                                }}
                            >
                                Показати доходи й витрати
                            </button>
                        )}
                    </div>
                )}
            </div>
            {/* TABLE */}
            {loading ? (
                <Loader />
            ) : (<div className="table-txs table-responsive">
                <table className="table table-transactions align-middle">
                    <thead>
                    <tr>
                        <th onClick={() => handleSort("time")}>Дата</th>
                        <th onClick={() => handleSort("amount")}>Сума</th>
                        <th onClick={() => handleSort("description")}>Опис</th>
                        {showTableIncome && showTableExpense && <th>Тип</th>}
                        <th>Категорія</th>
                        <th>Під категорія</th>
                        <th>Карта</th>
                        <th>Коментар</th>
                        <th className="text-nowrap text-end">Дії</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedTransactions.map(tx => (
                        <tr key={tx._id}>
                            <td>{formatDateToKyiv(tx.time)}</td>
                            <td>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            <pre>{JSON.stringify(tx, null, 2)}</pre>
                                        </Tooltip>
                                    }
                                >
                                    <strong style={{ cursor: "help" }}>
                                        {(tx.amount / 100).toFixed(2)} грн
                                    </strong>
                                </OverlayTrigger>
                            </td>
                            <td>
                                <div className="d-flex align-items-center justify-content-between">
                                    <span title={tx.description}>{formatTxDescription(tx.description)}</span>
                                    <CounterTags tx={tx} />
                                </div>
                            </td>
                            {showTableIncome && showTableExpense && (
                                <td>
                                    <span
                                        className={`tx-dir-dot tx-dir-dot--${tx.direction}`}
                                        title={tx.direction}
                                    />
                                </td>
                            )}
                            <td>
                                {user?.role === "admin" ? (
                                    <select
                                        value={tx.categoryType}
                                        className="form-select form-select-sm"
                                        onChange={(e) => handleTypeChange(tx, e.target.value)}
                                    >
                                        <option value="business">Бізнес</option>
                                        <option value="personal">Особисті</option>
                                    </select>
                                ) : (
                                    <span className="text-muted">
                                        {tx.categoryType === "business" && "Бізнес"}
                                        {tx.categoryType === "personal" && "Особисті"}
                                    </span>
                                )}
                            </td>
                            <td>
                                <select
                                    value={tx.category || ""}
                                    className="form-select form-select-sm"
                                    onChange={(e) =>
                                        handleCategoryChange(tx, e.target.value)
                                    }
                                >
                                    <option value="">Без категорії</option>
                                    {CATEGORIES[tx.categoryType]?.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </td>
                            <td className="text-end">
                                <CardLabel fop={tx.fop} cardType={tx.cardType} />
                            </td>
                            <td>
                                <input
                                    defaultValue={tx.comment}
                                    className="form-control form-control-sm"
                                    onBlur={(e) => handleCommentChange(tx._id, e.target.value)}
                                />
                            </td>
                            <td className="align-middle py-0 ps-1 text-end text-nowrap">
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="p-1 text-body text-decoration-none border-0 shadow-none"
                                    title="Редагувати"
                                    aria-label="Редагувати транзакцію"
                                    onClick={() => setEditTx(tx)}
                                >
                                    <Pencil size={18} aria-hidden />
                                </Button>
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                    title="Видалити"
                                    aria-label="Видалити транзакцію"
                                    onClick={() => {
                                        setDeleteId(tx._id);
                                        setShowConfirmDelete(true);
                                    }}
                                >
                                    <Trash2 size={18} aria-hidden />
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>)}

            {/* MODALS */}
            <ManualTransactionModal
                show={showManualPayment}
                onClose={() => setShowManualPayment(false)}
                onSubmit={() => {
                    void loadTransactions();
                }}
                user={user}
                presetCategoryType={type === "personal" ? "personal" : "business"}
                presetPaymentType={type === "cash" ? "cash" : undefined}
                presetCategory={
                    selectedCategory && selectedCategory !== "Без категорії"
                        ? selectedCategory
                        : undefined
                }
                presetDirection={
                    selectedDirection === "income" || selectedDirection === "expense"
                        ? selectedDirection
                        : undefined
                }
                presetTimeYMD={manualPresetTimeYMD}
            />
            <EditTransactionModal
                show={editTx}
                transaction={editTx}
                onChange={setEditTx}
                onClose={() => setEditTx(null)}
                onSave={async () => {
                    await updateTransaction(editTx._id, editTx);
                    setTransactions(prev =>
                        prev.map(t => t._id === editTx._id ? editTx : t)
                    );
                    setEditTx(null);
                }}
            />

            <Modal
                show={!!dayModal}
                onHide={() => setDayModal(null)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {dayModal &&
                            `${dayjs(dayModal.date).format("DD MMM YYYY")} — ${
                                dayModal.direction === "all"
                                    ? "Доходи та витрати"
                                    : dayModal.direction === "income"
                                        ? "Доходи"
                                        : "Витрати"
                            }`
                        }
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {dayModal?.transactions?.map(tx => (
                        <div key={tx._id} className="border-bottom py-2">
                            <div title={tx.description}>{formatTxDescription(tx.description)}</div>
                            <strong>{(tx.amount / 100).toFixed(2)} грн</strong>
                        </div>
                    ))}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDayModal(null)}>
                        Закрити
                    </Button>
                </Modal.Footer>
            </Modal>


            <DeleteTransactionModal
                show={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={async () => {
                    await deleteTransaction(deleteId);
                    setTransactions(prev => prev.filter(t => t._id !== deleteId));
                    setShowConfirmDelete(false);
                }}
            />
        </div>
    );
};

export default TransactionsByTypePage;
